import { Link } from 'react-router-dom';
import { formatRelativeTime, formatPrice, getAmountFromPriceObject } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import type { Order } from '@/lib/types';

import { Card, CardContent, CardHeader } from '@/components/atoms/card';
import { StatusBadge } from '@/components/molecules/status-badge';

interface OrderCardProps {
  order: Order;
  locale?: string;
}

// Extract customer name from string or object
function getCustomerName(customer: Order['customer']): string {
  if (!customer) return '-';
  if (typeof customer === 'string') return customer;
  if (typeof customer === 'object') {
    if (customer.fullName) return customer.fullName;
    const parts = [customer.firstName, customer.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '-';
  }
  return '-';
}

export function OrderCard({ order, locale = 'fr' }: OrderCardProps) {
  const { currency } = useConfigStore();
  const productCount = order.products?.length ?? 0;

  return (
    <Link to={`/orders/${order.orderId}`}>
      <Card className="hover:border-primary/50 transition-colors cursor-pointer">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-medium">
                #{order.orderId}
              </span>
              <StatusBadge status={order.status} />
            </div>
            <span className="text-sm text-muted-foreground">
              {formatRelativeTime(order.orderDate, locale)}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{getCustomerName(order.customer)}</p>
              <p className="text-sm text-muted-foreground">
                {productCount} article{productCount > 1 ? 's' : ''}
              </p>
            </div>
            <p className="text-lg font-bold">{formatPrice(getAmountFromPriceObject(order.total_price, currency), currency)}</p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
